import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  formatInterviewDateTime,
  getDefaultInterviewDate,
  isDateInFuture,
  getTimeRemaining,
} from "../../../utils/dateUtils";
import WelcomeHeader from "./WelcomeHeader";
import emailImg from "../../../assets/images/nh_consumer_all_set.svg";
import logoImg from "../../../assets/images/logo.svg";
import { getUrlParams } from "../../../utils/urlUtils";
import Header from "../../commonComponents/header/Header";
import { maskText } from "../../commonComponents/helperFunctions/HelperFunction.js";
import Footer from "../../commonComponents/footer/Footer.js";
import bgImage from "../../../assets/images/acceptance-bg.svg";
import { transform } from "framer-motion";

const WelcomeResponse = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from navigation state instead of API call
  const navigationData = location.state;

  const [urlParams, setUrlParams] = useState({
    resumeId: null,
    jobDescriptionId: null,
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastDate, setLastDate] = useState(null);
  const [candidateName, setCandidateName] = useState(null);
  const [emailSentTo, setEmailSentTo] = useState(null);
  const [companyName, setCompanyName] = useState(null);

  useEffect(() => {
    // Check if we have navigation data
    if (navigationData) {
      console.log("Navigation data:", navigationData);
      // Set data from navigation state
      setUrlParams({
        resumeId: navigationData.resumeId,
        jobDescriptionId: navigationData.jobDescriptionId,
      });

      setLastDate(navigationData.lastDate);
      setCandidateName(navigationData.candidateName);
      setEmailSentTo(navigationData.emailSentTo);
      setCompanyName(navigationData.companyName);
      setIsInitialLoad(false);
    } else {
      // Fallback: try to get from URL params (for backward compatibility)
      console.warn("No navigation data found, trying URL params");
      const params = getUrlParams();

      setUrlParams(params);
      setIsInitialLoad(false);
    }
  }, [navigationData]);

  // Determine what date to display
  const getDisplayDate = () => {
    if (lastDate) {
      const formattedDate = formatInterviewDateTime(lastDate);
      return formattedDate || getDefaultInterviewDate();
    }
    return getDefaultInterviewDate(); // Default fallback
  };

  // Get time remaining display and check if deadline has passed
  const getTimeRemainingDisplay = () => {
    if (!lastDate) return null;
    return getTimeRemaining(lastDate);
  };

  // Check if interview deadline has passed
  const isInterviewExpired = () => {
    if (!lastDate) return false;
    return !isDateInFuture(lastDate);
  };

  // Show loading screen while processing initial data
  if (isInitialLoad) {
    return (
      <div className="flex flex-col h-screen bg-white">
        {/* Fixed Header */}
        <WelcomeHeader />

        {/* Loading Content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <p className="text-lg text-gray-600">
              Loading assessment details...
            </p>
            <p className="text-sm text-gray-500 mt-2 font-normal">
              Please wait while we process your information
            </p>
          </div>
        </div>

        {/* Footer */}
        <Footer />
        {/* <div className="bg-white py-4 text-[10px] text-gray-600">
          <div className="flex justify-center">
            © 2025, Needles and Haystacks Technologies.
          </div>
          {/* <div className="absolute right-4 bottom-4 flex items-center space-x-1 text-[#002147] font-semibold">
            <span>Powered by</span>
            <img
              src={logo}
              alt="logo"
              className="w-5 h-5 filter brightness-0 invert-0"
            />
            <span>Needles & Haystacks</span>
          </div> */}
        {/* </div>  */}
      </div>
    );
  }

  // Show main content after data is loaded
  return (
    <div className="flex flex-col h-screen bg-white px-4 pt-4 box-border overflow-hidden">
      {/* Fixed Header */}
      <Header />

      {/* Main Content */}
      <div
        className="mt-10 md:mt-0 flex flex-col md:flex-row flex-1 items-center justify-center gap-6 overflow-y-auto py-4"
        style={{ maxHeight: "calc(100vh - 45px)" }}
      >
        {/* Left side image */}
        <div className="flex items-center justify-center md:flex-1 md:pr-4">
          <img
            src={emailImg}
            alt="Email sent"
            className="max-w-[260px] md:max-w-[500px] w-full h-auto object-contain"
          />
        </div>

        {/* Divider — hidden on mobile */}
        <div className="hidden md:block border-l border-dividerColor h-[70%] mx-6" />

        {/* Right side text */}
        <div className="flex-1 px-2 md:pl-8 flex flex-col gap-6">
          {isInterviewExpired() ? (
            <>
              <h1 className="app-title font-medium text-errorColor">
                Assessment Deadline Has Passed
              </h1>
              <p className="app-body font-medium text-base text-errorColor">
                Unfortunately, the deadline for this interview has expired.
              </p>
              <p className="app-textbox">
                The assessment deadline was{" "}
                <span className="font-semibold text-white bg-[#F00936] px-[15px] rounded-full text-center inline-block">
                  {getDisplayDate()}
                </span>
                . You can no longer access the assessment for this position.
              </p>
            </>
          ) : (
            <>
              <h1 className="app-title font-medium">
                Thank you
                <span>
                  {candidateName
                    ? ", " +
                      candidateName
                        .toLowerCase()
                        .replace(/\b\w/g, (c) => c.toUpperCase())
                    : ""}
                </span>
                {/* ! Please check your Email. */}
              </h1>
              {/* <p className="app-body text-base">
                An email with a secure link has been sent to your email address
                <span className="font-semi-500">
                  {emailSentTo ? ` (${maskText(emailSentTo)})` : ""}
                </span>
                .
              </p> */}
              {/* <p className="app-textbox">
                Please note that the Last Date and Time to attend your
                Assessment is{" "}
                <span className="font-semibold text-white bg-[#F00936] px-[15px] rounded-full text-center inline-block">
                  {getDisplayDate()}
                </span>
              </p> */}
              {/* <p className="app-textbox">
                You can Login and attend your Assessment any time on or before
                the Last Date and Time.
              </p> */}
              <p className="app-textbox">
                We at{" "}
                <span className="font-semibold text-[#002147]">
                  {companyName || "ABC Technology Pvt Ltd"}
                </span>{" "}
                wish you all the very best. Good luck!
              </p>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WelcomeResponse;
